import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/warehouse/warehouseAdd.module.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createWarehouse, getCountries, getStates, getCities } from '../../services/Api';
import PopUpMessage from '../../utils/errors/PopUpMessage';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import { setShouldRefresh } from '../../features/warehouse/warehouseSlice';
import { AiOutlineLoading } from 'react-icons/ai';

const WarehouseAdd = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState({
        countries: false,
        states: false,
        cities: false
    });

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        status: ''
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const validationRules = {
        name: {
            required: true,
            message: 'Name is required'
        },
        address: {
            required: true,
            message: 'Address is required'
        },
        country_id: {
            required: true,
            message: 'Country is required'
        },
        state_id: {
            required: true,
            message: 'State is required'
        },
        city_id: {
            required: true,
            message: 'City is required'
        },
        status: {
            required: true,
            message: 'Please select a status'
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: value
            };

            // Reset dependent dropdowns when parent changes
            if (name === 'country_id') {
                newData.state_id = '';
                newData.city_id = '';
                setStates([]); // Clear states when country changes
                setCities([]); // Clear cities when country changes
            }
            if (name === 'state_id') {
                newData.city_id = '';
                setCities([]); // Clear cities when state changes
            }

            return newData;
        });

        // Mark field as touched when changed
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Style select elements based on value
        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        // Clear errors for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        if (value.trim() !== '') {
            validateField(name, value);
        }
    };

    const validateField = (name, value) => {
        const rule = validationRules[name];
        if (!rule) return true;

        if (rule.required && !value) {
            setErrors(prev => ({
                ...prev,
                [name]: rule.message
            }));
            return false;
        }

        if (rule.pattern && value && !rule.pattern.test(value)) {
            setErrors(prev => ({
                ...prev,
                [name]: rule.message
            }));
            return false;
        }

        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
        return true;
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Mark all fields as touched when submitting
        const allTouched = {};
        Object.keys(validationRules).forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);

        Object.keys(validationRules).forEach(field => {
            const rule = validationRules[field];
            const value = formData[field];

            // Check required fields
            if (rule.required && !value) {
                newErrors[field] = rule.message;
                isValid = false;
                return;
            }

            // Check pattern matching (if specified)
            if (rule.pattern && value && !rule.pattern.test(value)) {
                newErrors[field] = rule.message;
                isValid = false;
                return;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        // First validate the form
        if (!validateForm()) {
            showPopup('error', 'Validation Error', 'Please correct the errors in the form');
            return;
        }

        setLoading(true);

        try {
            const apiData = {
                name: formData.name,
                address: formData.address,
                country_id: formData.country_id,
                state_id: formData.state_id,
                city_id: formData.city_id,
                status: formData.status === '1' ? '1' : '0',
            };

            const response = await createWarehouse(apiData);

            if (response.status === 200) {
                showPopup('success', 'Success', 'Warehouse created successfully');
                dispatch(setShouldRefresh(true));
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/warehouse");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to create warehouse';
            showPopup('error', 'Error', errorMessage);

            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                // Mark fields with errors as touched
                const newTouched = {};
                Object.keys(error.response.data.errors).forEach(field => {
                    newTouched[field] = true;
                });
                setTouched(prev => ({ ...prev, ...newTouched }));
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper function to determine if a field should show error styling
    const shouldShowError = (fieldName) => {
        return touched[fieldName] && errors[fieldName];
    };

    // Fetch countries on component mount
    useEffect(() => {
        const fetchCountries = async () => {
            setLoadingLocations(prev => ({ ...prev, countries: true }));
            try {
                const response = await getCountries();
                console.log('Countries API Response:', response); // Debug log

                // Handle different possible response structures
                let countriesData = [];
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    countriesData = response.data.data;
                } else if (response?.data && Array.isArray(response.data)) {
                    countriesData = response.data;
                } else if (Array.isArray(response)) {
                    countriesData = response;
                }

                if (countriesData.length > 0) {
                    setCountries(countriesData);
                } else {
                    console.error("No countries data found in response:", response);
                    showPopup('error', 'Error', 'No countries data available');
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
                showPopup('error', 'Error', 'Failed to load countries');
            } finally {
                setLoadingLocations(prev => ({ ...prev, countries: false }));
            }
        };

        fetchCountries();
    }, []);

    // Fetch states when country changes
    useEffect(() => {
        const fetchStates = async () => {
            if (!formData.country_id) {
                setStates([]);
                return;
            }

            setLoadingLocations(prev => ({ ...prev, states: true }));
            try {
                const response = await getStates(formData.country_id);
                console.log('States API Response:', response); // Debug log

                // Handle different possible response structures
                let statesData = [];
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    statesData = response.data.data;
                } else if (response?.data && Array.isArray(response.data)) {
                    statesData = response.data;
                } else if (Array.isArray(response)) {
                    statesData = response;
                }

                setStates(statesData);
            } catch (error) {
                console.error("Error fetching states:", error);
                showPopup('error', 'Error', 'Failed to load states');
                setStates([]);
            } finally {
                setLoadingLocations(prev => ({ ...prev, states: false }));
            }
        };

        fetchStates();
    }, [formData.country_id]);

    // Fetch cities when state changes
    useEffect(() => {
        const fetchCities = async () => {
            if (!formData.state_id) {
                setCities([]);
                return;
            }

            setLoadingLocations(prev => ({ ...prev, cities: true }));
            try {
                const response = await getCities(formData.state_id);
                console.log('Cities API Response:', response); // Debug log

                // Handle different possible response structures
                let citiesData = [];
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    citiesData = response.data.data;
                } else if (response?.data && Array.isArray(response.data)) {
                    citiesData = response.data;
                } else if (Array.isArray(response)) {
                    citiesData = response;
                }

                setCities(citiesData);
            } catch (error) {
                console.error("Error fetching cities:", error);
                showPopup('error', 'Error', 'Failed to load cities');
                setCities([]);
            } finally {
                setLoadingLocations(prev => ({ ...prev, cities: false }));
            }
        };

        fetchCities();
    }, [formData.state_id]);

    return (
        <div className={`${Styles.warehouse_add}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Create Warehouse</h2>
            </div>

            <div className={`${Styles.warehouse_form}`}>
                <div className={`${Styles.warehouse_details}`}>
                    <div className={`${Styles.title}`}>
                        <h4>Warehouse details</h4>
                        <hr />
                    </div>
                    <div className={`${Styles.form}`}>
                        <div className={`${Styles.label_input} ${shouldShowError('name') ? Styles.error : ''}`}>
                            <label>Warehouse Name<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    placeholder="Enter Warehouse Name"
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={shouldShowError('name') ? Styles.errorInput : ''}
                                />
                                {shouldShowError('name') &&
                                    <span className={`${Styles.errorMessage}`}>{errors.name}</span>
                                }
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${shouldShowError('address') ? Styles.error : ''}`}>
                            <label>Warehouse Address<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    placeholder="Enter Address"
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={shouldShowError('address') ? Styles.errorInput : ''}
                                />
                                {shouldShowError('address') &&
                                    <span className={`${Styles.errorMessage}`}>{errors.address}</span>
                                }
                            </div>
                        </div>

                        <div className={`${Styles.label_input}`}>
                            <label>Country / State / City<span>*</span></label>
                            <div className={`${Styles.select_container}`}>
                                <div className={`${Styles.country}`}>
                                    <select
                                        name="country_id"
                                        value={formData.country_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        className={shouldShowError('country_id') ? Styles.errorInput : ''}
                                        disabled={loadingLocations.countries}
                                    >
                                        <option value="" hidden>
                                            {loadingLocations.countries ? 'Loading countries...' : 'Select Country'}
                                        </option>
                                        {countries.map((country) => (
                                            <option key={country.id} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    {shouldShowError('country_id') &&
                                        <span className={`${Styles.errorMessage}`}>{errors.country_id}</span>
                                    }
                                </div>

                                <div className={`${Styles.state}`}>
                                    <select
                                        name="state_id"
                                        value={formData.state_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        disabled={!formData.country_id || loadingLocations.states}
                                        className={shouldShowError('state_id') ? Styles.errorInput : ''}
                                    >
                                        <option value="" hidden>
                                            {loadingLocations.states
                                                ? 'Loading states...'
                                                : 'Select State'
                                            }
                                        </option>
                                        {states.map((state) => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </select>
                                    {shouldShowError('state_id') &&
                                        <span className={`${Styles.errorMessage}`}>{errors.state_id}</span>
                                    }
                                </div>

                                <div className={`${Styles.city}`}>
                                    <select
                                        name="city_id"
                                        value={formData.city_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        disabled={!formData.state_id || loadingLocations.cities}
                                        className={shouldShowError('city_id') ? Styles.errorInput : ''}
                                    >
                                        <option value="" hidden>
                                            {loadingLocations.cities
                                                ? 'Loading cities...'
                                                : 'Select City'
                                            }
                                        </option>
                                        {cities.map((city) => (
                                            <option key={city.id} value={city.id}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                    {shouldShowError('city_id') &&
                                        <span className={`${Styles.errorMessage}`}>{errors.city_id}</span>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${shouldShowError('status') ? Styles.error : ''}`}>
                            <label>Status<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={shouldShowError('status') ? Styles.errorInput : ''}
                                >
                                    <option value="" hidden>Select Status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                                {shouldShowError('status') &&
                                    <span className={`${Styles.errorMessage}`}>{errors.status}</span>
                                }
                            </div>
                        </div>
                    </div>

                    <div className={`${Styles.buttons}`}>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? <AiOutlineLoading /> : "Create"}
                        </button>
                        <button onClick={() => navigate("/warehouse")}>Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WarehouseAdd;